import {Form} from '../types';

/**
 * Attaching an existing record to a one-to-many cell is described by the
 * related screen's create form; the screen must also be marked as a
 * many-to-many link in admin, so a form that merely looks like a link does
 * not turn the cell into a multi-picker.
 *
 * A form that has exactly one visible field, a many-to-one pointing at a screen,
 * describes nothing but a link: that field says what to pick and where from, and
 * every other field carries a `context` (which is why the user never sees them)
 * and is filled from the published context filters, the parent key among them.
 *
 * Attaching then means creating such a row, detaching means deleting it — the
 * picked record itself is never touched.
 */
export interface AssignShape {
    /** Screen listing the candidates (the far side of the link). */
    pickerScreenId: string;
    /** Screen over the link rows — deleting one of its rows detaches. */
    linkScreenId: string;
    /** Form that creates the link; its context fields carry the parent. */
    createForm: Form;
    /** Key the picked id is posted under. */
    targetFieldCode: string;
    /** Key the target sits under in a link screen's own rows. */
    targetColumnCode: string;
    /** Filter keys the link screen receives the parent record under. */
    parentFilterCodes: string[];
    /** Whether a link row may be deleted, i.e. whether detaching is offered. */
    canDetach: boolean;
}

export const resolveAssignShape = (column: any): AssignShape | null => {
    const relatedScreen = column?.relatedScreen;
    const createForm = (relatedScreen?.createForms || [])[0];
    if (!relatedScreen?.id || !relatedScreen.manyToMany || !createForm?.id) return null;

    const fields = createForm.fields || [];
    const visible = fields.filter((f: any) => !f.context && f.type !== 'one-to-many');
    const target = visible.length === 1 && visible[0].type === 'many-to-one' && visible[0].screenId
        ? visible[0]
        : null;
    if (!target || !fields.some((f: any) => f.context)) return null;

    const parentFilterCodes: string[] = (relatedScreen.filters || []).map((f: any) => f.code);
    const targetColumn = resolveTargetColumn(relatedScreen, target, parentFilterCodes);

    return {
        pickerScreenId: target.screenId,
        linkScreenId: relatedScreen.id,
        createForm,
        targetFieldCode: target.code,
        targetColumnCode: targetColumn?.code || target.code,
        parentFilterCodes,
        canDetach: !!relatedScreen.deleteEnabled,
    };
};

const SYSTEM_COLUMNS = new Set(['id', 'created_at', 'updated_at']);

/**
 * The link screen's column the picked record lands in - the key it sits
 * under in that screen's rows.
 *
 * A screen-bound form names it outright through screenColumnId. A
 * table-bound one (DynamicForm) does not: its field code is the column's
 * name in the table, which need not be the screen column's code (cars_id vs
 * car_id). The link screen itself still tells: it shows two many-to-one
 * columns, the parent is the one fed from context, so the other is the
 * target.
 */
const resolveTargetColumn = (relatedScreen: any, target: any, parentFilterCodes: string[]): any | null => {
    const columns: any[] = relatedScreen.columns || [];

    if (target.screenColumnId) {
        const bound = columns.find(c => c.id === target.screenColumnId);
        if (bound) return bound;
    }

    const own = columns.filter(c => !SYSTEM_COLUMNS.has(c.code));
    const relations = own.filter(c => c.columnType === 'many-to-one');
    if (own.length !== 2 || relations.length !== 2) return null;

    const isParent = (c: any) => (c.contextCode && parentFilterCodes.includes(c.contextCode)) || c.screenHidden;
    const parents = relations.filter(isParent);
    if (parents.length !== 1) return null;

    return relations.find(c => c !== parents[0]) || null;
};
